/**
 * Shopify Shipping Quote App - Theme Integration (v2 - Enhanced Tag Detection)
 * 
 * This version includes better tag detection and detailed logging.
 * 
 * Installation:
 * 1. Upload this file to your Shopify theme assets
 * 2. Add to theme.liquid before </body>:
 *    <script src="{{ 'shopify-quote-integration-v2.js' | asset_url }}" defer></script>
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    QUOTE_APP_URL: 'https://shipquote-hdwabfsa.manus.space', // UPDATE THIS!
    REQUIRED_TAG: 'require-shipping',
    BUTTON_TEXT: 'Request Shipping Quote',
    DEBUG: true,
  };

  function log(message, data) {
    if (CONFIG.DEBUG) {
      console.log(`[Quote Integration] ${message}`, data !== undefined ? data : '');
    }
  }

  function error(message, data) {
    console.error(`[Quote Integration ERROR] ${message}`, data !== undefined ? data : '');
  }

  /**
   * Check if a tag matches (case-insensitive, trimmed)
   */
  function tagMatches(tag, requiredTag) {
    if (!tag) return false;
    const tagStr = String(tag).toLowerCase().trim();
    const requiredStr = String(requiredTag).toLowerCase().trim();
    return tagStr === requiredStr;
  }

  /**
   * Check if product has the require-shipping tag
   */
  function hasRequireShippingTag() {
    log('=== Checking for require-shipping tag ===');

    // Method 1: ShopifyAnalytics
    if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
      log('Method 1: ShopifyAnalytics.meta.product');
      const tags = ShopifyAnalytics.meta.product.tags || [];
      log('  Tags:', tags);
      log('  Tags JSON:', JSON.stringify(tags));
      
      for (const tag of tags) {
        if (tagMatches(tag, CONFIG.REQUIRED_TAG)) {
          log('  ✓ MATCH FOUND in ShopifyAnalytics');
          return true;
        }
      }
    }

    // Method 2: data-product-json attribute
    const productJson = document.querySelector('[data-product-json]');
    if (productJson) {
      log('Method 2: [data-product-json] element');
      try {
        const product = JSON.parse(productJson.textContent);
        log('  Product:', product);
        log('  Tags:', product.tags);
        log('  Tags JSON:', JSON.stringify(product.tags));
        log('  Tags type:', typeof product.tags);
        log('  Tags is array:', Array.isArray(product.tags));
        
        if (product.tags && Array.isArray(product.tags)) {
          log('  Tag count:', product.tags.length);
          product.tags.forEach((tag, index) => {
            log(`  Tag[${index}]:`, tag, `(type: ${typeof tag})`);
          });
          
          for (const tag of product.tags) {
            if (tagMatches(tag, CONFIG.REQUIRED_TAG)) {
              log('  ✓ MATCH FOUND in data-product-json');
              return true;
            }
          }
        }
      } catch (e) {
        error('  Failed to parse product JSON', e);
      }
    }

    // Method 3: script tag with product JSON
    const scriptTag = document.querySelector('script[type="application/json"][data-product-json]');
    if (scriptTag) {
      log('Method 3: script tag with product JSON');
      try {
        const product = JSON.parse(scriptTag.textContent);
        log('  Product:', product);
        log('  Tags:', product.tags);
        
        if (product.tags && Array.isArray(product.tags)) {
          for (const tag of product.tags) {
            if (tagMatches(tag, CONFIG.REQUIRED_TAG)) {
              log('  ✓ MATCH FOUND in script tag');
              return true;
            }
          }
        }
      } catch (e) {
        error('  Failed to parse script tag', e);
      }
    }

    // Method 4: window.meta
    if (typeof window.meta !== 'undefined' && window.meta.product) {
      log('Method 4: window.meta.product');
      const tags = window.meta.product.tags || [];
      log('  Tags:', tags);
      
      for (const tag of tags) {
        if (tagMatches(tag, CONFIG.REQUIRED_TAG)) {
          log('  ✓ MATCH FOUND in window.meta');
          return true;
        }
      }
    }

    // Method 5: meta tags
    const metaTags = document.querySelectorAll('meta[property="product:tag"]');
    if (metaTags.length > 0) {
      log('Method 5: meta tags');
      for (const meta of metaTags) {
        log('  Meta tag content:', meta.content);
        if (tagMatches(meta.content, CONFIG.REQUIRED_TAG)) {
          log('  ✓ MATCH FOUND in meta tags');
          return true;
        }
      }
    }

    log('✗ No matching tag found');
    log(`Looking for: "${CONFIG.REQUIRED_TAG}"`);
    return false;
  }

  /**
   * Get product and variant info
   */
  function getProductInfo() {
    log('Getting product info...');
    let product = null;
    let variant = null;

    // Try ShopifyAnalytics
    if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta) {
      product = ShopifyAnalytics.meta.product;
      variant = ShopifyAnalytics.meta.selectedVariant;
    }

    // Try product JSON
    if (!product) {
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        try {
          product = JSON.parse(productJson.textContent);
        } catch (e) {
          error('Failed to parse product JSON', e);
        }
      }
    }

    // Try script tag
    if (!product) {
      const scriptTag = document.querySelector('script[type="application/json"][data-product-json]');
      if (scriptTag) {
        try {
          product = JSON.parse(scriptTag.textContent);
        } catch (e) {
          error('Failed to parse script tag', e);
        }
      }
    }

    // Try window.product
    if (!product && typeof window.product !== 'undefined') {
      product = window.product;
    }

    // Get variant
    if (!variant && product) {
      const variantIdInput = document.querySelector('input[name="id"], select[name="id"], [name="id"]');
      if (variantIdInput) {
        const variantId = variantIdInput.value;
        if (product.variants) {
          variant = product.variants.find(v => v.id == variantId) || product.variants[0];
        }
      } else if (product.variants) {
        variant = product.variants[0];
      }
    }

    log('Product:', product);
    log('Variant:', variant);
    return { product, variant };
  }

  /**
   * Build quote URL
   */
  function buildQuoteUrl(product, variant) {
    const params = new URLSearchParams({
      productId: product.id,
      variantId: variant.id,
      productTitle: product.title,
      variantTitle: variant.title || '',
      sku: variant.sku || '',
      price: variant.price,
      weight: variant.weight || '',
      weightUnit: variant.weight_unit || 'kg',
    });

    return `${CONFIG.QUOTE_APP_URL}/quote/request?${params.toString()}`;
  }

  /**
   * Replace Add to Cart button
   */
  function replaceAddToCartButton() {
    log('Searching for Add to Cart button...');

    const selectors = [
      'button[name="add"]',
      'button[type="submit"][name="add"]',
      'input[type="submit"][name="add"]',
      'button[data-action="add-to-cart"]',
      'button[data-add-to-cart]',
      '.product-form__submit',
      '.product-form__cart-submit',
      '.product__submit',
      '.add-to-cart-button',
      '.btn--add-to-cart',
      'form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] input[type="submit"]',
      '#AddToCart',
      '#add-to-cart',
      '.add-to-cart',
    ];

    let addToCartButton = null;
    let usedSelector = null;
    
    for (const selector of selectors) {
      const buttons = document.querySelectorAll(selector);
      for (const btn of buttons) {
        if (btn.offsetParent !== null) {
          addToCartButton = btn;
          usedSelector = selector;
          break;
        }
      }
      if (addToCartButton) break;
    }

    if (!addToCartButton) {
      error('Could not find Add to Cart button');
      return;
    }

    log(`✓ Found button with selector: ${usedSelector}`);

    if (addToCartButton.classList.contains('quote-request-btn') || 
        addToCartButton.hasAttribute('data-quote-button')) {
      log('Button already replaced');
      return;
    }

    const { product, variant } = getProductInfo();
    if (!product || !variant) {
      error('Cannot replace button without product/variant info');
      return;
    }

    const quoteButton = document.createElement('a');
    quoteButton.href = buildQuoteUrl(product, variant);
    quoteButton.textContent = CONFIG.BUTTON_TEXT;
    quoteButton.className = addToCartButton.className + ' quote-request-btn';
    quoteButton.setAttribute('data-quote-button', 'true');
    quoteButton.style.cssText = addToCartButton.style.cssText;

    Array.from(addToCartButton.attributes).forEach(attr => {
      if (attr.name.startsWith('data-') && attr.name !== 'data-quote-button') {
        quoteButton.setAttribute(attr.name, attr.value);
      }
    });

    try {
      addToCartButton.parentNode.replaceChild(quoteButton, addToCartButton);
      log('✓ Button replaced successfully!');
    } catch (e) {
      error('Failed to replace button', e);
      return;
    }

    // Update on variant change
    const updateQuoteUrl = () => {
      setTimeout(() => {
        const { product: newProduct, variant: newVariant } = getProductInfo();
        if (newProduct && newVariant) {
          quoteButton.href = buildQuoteUrl(newProduct, newVariant);
          log('Updated URL for new variant');
        }
      }, 100);
    };

    document.addEventListener('change', function(e) {
      if (e.target.name === 'id' || e.target.closest('[data-product-form]') || e.target.closest('.product-form')) {
        updateQuoteUrl();
      }
    });

    document.addEventListener('variant:change', updateQuoteUrl);
    document.addEventListener('variantChange', updateQuoteUrl);
  }

  /**
   * Initialize
   */
  function init() {
    log('=== Shopify Quote Integration v2 Starting ===');
    log('Config:', CONFIG);
    log('URL:', window.location.href);
    log('Body classes:', document.body.className);

    const isProductPage = document.body.classList.contains('template-product') ||
                         document.body.classList.contains('product-template') ||
                         window.location.pathname.includes('/products/');

    if (!isProductPage) {
      log('Not a product page');
      return;
    }

    log('✓ On product page');

    if (!hasRequireShippingTag()) {
      log('Product does not have require-shipping tag');
      return;
    }

    log('✓ Product has require-shipping tag!');
    replaceAddToCartButton();
    log('=== Initialization Complete ===');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  setTimeout(() => {
    if (hasRequireShippingTag()) {
      replaceAddToCartButton();
    }
  }, 1000);

  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => {
      if (hasRequireShippingTag()) {
        replaceAddToCartButton();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

})();
